import classNames from "classnames";
import { deepmerge } from "~/common/mui/utils";

/**
 * Merges multiple sets of props for a MUI "slot" (e.g., label, value, input).
 *
 * Rules:
 * - Recursively merges objects using MUI's internal `deepmerge`.
 * - Specifically handles `className` by concatenating strings via `classNames`.
 * - Later arguments take precedence for conflicting non-object properties.
 *
 * @example
 * const merged = mergeSlotProps(
 *   { variant: 'body2', sx: { color: 'red' }, className: 'base' },
 *   { sx: { mt: 2 }, className: 'override' }
 * );
 * // Result: { variant: 'body2', sx: { color: 'red', mt: 2 }, className: 'base override' }
 *
 * @param props - An arbitrary number of prop objects to merge.
 * @returns A single merged prop object.
 */
export const mergeSlotProps = <T extends { className?: string }>(...props: (T | undefined)[]): T => {
  const merged = props.reduce((acc, curr) => {
    if (!curr) {
      return acc;
    }
    return deepmerge(acc, curr);
  }, {} as T) as T;

  // Deepmerge overwrites strings, so we manually fix the className
  const className = classNames(...props.map((p) => p?.className));
  if (className) {
    return { ...merged, className };
  }

  return merged;
};
